import { DominoConfig } from '@models/venue/integrations/DominoConfig'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

import { dominoConfigSchema } from '@models/schema'
import {
  createDominoConfig as createDominoConfigAPI,
  deleteDominoConfig as deleteDominoConfigAPI,
  fetchDominoConfigById,
  fetchDominoConfigs,
  updateDominoConfig as updateDominoConfigAPI,
} from '@services/venue/integrations/DominoConfigService'
import { AppDispatch, RootState } from '../../store'

const initialState: SBState<DominoConfig> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'dominoConfig',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getDominoConfigsSuccess,
  setQuery: setDominoConfigQuery,
  reset: resetDominoConfigState,
  resetQueryAndIds: resetDominoConfigQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: DominoConfig }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectDominoConfigs = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedDominoConfig = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectDominoConfigById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectDominoConfigsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedDominoConfig =
  (DominoConfig: DominoConfig | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<DominoConfig>(DominoConfig))
    )

export const getDominoConfigs = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchDominoConfigs(params),
    [dominoConfigSchema],
    slice.actions
  )

export const createDominoConfig = (DominoConfig: DominoConfig) =>
  SBAPI.CreateDispatched(
    createDominoConfigAPI(DominoConfig),
    dominoConfigSchema,
    slice.actions
  )

export const getDominoConfigWithId = (id: number) =>
  SBAPI.FetchDispatched<DominoConfig>(
    fetchDominoConfigById(id),
    dominoConfigSchema,
    slice.actions
  )

export const updateDominoConfig = (DominoConfig: DominoConfig) =>
  SBAPI.UpdateDispatched<DominoConfig>(
    updateDominoConfigAPI(DominoConfig),
    DominoConfig,
    slice.actions
  )

export const deleteDominoConfig = (DominoConfig: DominoConfig) =>
  SBAPI.DestroyDispatched<DominoConfig>(
    deleteDominoConfigAPI(DominoConfig),
    DominoConfig,
    slice.actions
  )
